import { Container, Divider, Tooltip } from "@material-ui/core";
import { Grid, Typography } from "@mui/material";
import axios from "axios";
import {
  ArrayChip,
  AutoCompleteWithSearch,
  ButtonGroupTop,
  CustomTable,
  ModalError,
  ModalSuccess,
  Page,
  PrimaryButton,
  SearchTextInput,
  SelectInput,
} from "components";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { getErrors } from "utils";
import { breadcrumbData } from "../Components/SongClaimMenu";
import ModalFormOwnershipConflict from "./Components/ModalFormOwnershipConflict";

const OwnershipConflict = ({ userLogin }) => {
  const classes = globalStyles();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingFilter, setLoadingFilter] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [optionFilter, setOptionFilter] = useState([]);
  const [dataTable, setDataTable] = useState([]);
  const [dynamicFilterKey, setDynamicFilterKey] = useState("");
  const [queryParams, setQueryParams] = useState({
    page: 1,
    size: 10,
    search: "",
  });
  const [filterParams, setFilterParams] = useState({
    page: 1,
    size: 10,
    search: "",
    filter_type: "",
  });
  const [hasMore, setHasMore] = useState(true);
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalPreload, setModalPreload] = useState(null);
  const rolePublisher = userLogin?.role?.type === "publisher";
  const selectedOptionFilter = optionType({ rolePublisher })?.find(
    option => option?.key === filterParams?.filter_type
  );

  const handleChangeQueryParams = (value, key) => {
    const isDefaultKey = defaultKeys.includes(key);
    setQueryParams(currentState => {
      const updated = { ...currentState };
      if (
        !isDefaultKey &&
        dynamicFilterKey &&
        dynamicFilterKey !== key &&
        !defaultKeys.includes(dynamicFilterKey)
      ) {
        delete updated[dynamicFilterKey];
      }
      return {
        ...updated,
        ...(key !== "page" && { page: 1 }),
        [key]: value,
      };
    });
    if (!isDefaultKey) {
      setDynamicFilterKey(key);
    }
  };

  const handleChangeFilterParams = (value, key) => {
    setFilterParams(currentState => ({
      ...currentState,
      [key]: value,
    }));
  };
  const handleChangeFilterType = e => {
    setOptionFilter([]);
    handleChangeFilterParams(e, "filter_type");
    setQueryParams(prev => {
      const updated = { ...prev };
      delete updated[dynamicFilterKey];
      return updated;
    });
    setDynamicFilterKey("");
  };
  const handleOpenModal = preload => {
    setModalPreload(preload);
    setModalVisible(true);
  };
  const handleCloseModal = () => {
    setModalPreload(null);
    setModalVisible(false);
  };

  const getDataTable = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(`${hardBaseUrl}/songs/song-conflict`, {
        headers,
        params: queryParams,
      });

      const { data, meta } = res?.data;
      setDataTable(data);
      const pageCount = getTotalPage(meta?.total, queryParams?.size || 1);
      setTableTotalPage(pageCount);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const debounceDataTable = useCallback(
    debounce(() => {
      getDataTable();
    }, 500),
    [queryParams]
  );
  const getOptionFilter = async () => {
    try {
      setLoadingFilter(true);
      const res = await axios.get(`${hardBaseUrl}/songs/song-conflict/filter`, {
        headers,
        params: filterParams,
      });
      const { data, meta } = res?.data;
      const newOptions = (data || [])?.map(item => ({
        ...item,
        id: item?.value,
      }));
      setOptionFilter(prev => {
        const combined =
          filterParams.page === 1 ? newOptions : [...prev, ...newOptions];
        setHasMore(meta?.total > combined.length);
        return combined;
      });
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingFilter(false);
    }
  };
  const debounceOptionFilter = useCallback(
    debounce(() => {
      getOptionFilter();
    }, 500),
    [filterParams]
  );
  const handleDownloadDocument = async () => {
    try {
      setLoadingButton(true);
      const { data } = await axios.post(
        `${hardBaseUrl}/songs/song-conflict/export  `,
        {},
        { headers }
      );
      ModalSuccess(data?.message, "Data is being processed");
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingButton(false);
    }
  };

  useEffect(() => {
    const isSearching = Boolean(queryParams?.search);
    if (isSearching) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      getDataTable();
    }
  }, [queryParams, debounceDataTable]);
  useEffect(() => {
    const isSearching = Boolean(filterParams?.search);
    if (isSearching) {
      debounceOptionFilter();
      return () => {
        debounceOptionFilter.cancel();
      };
    } else {
      getOptionFilter();
    }
  }, [filterParams, debounceOptionFilter]);

  return (
    <Page className={classes.root} title="Ownership Conflict">
      <Container maxWidth={false}>
        <HeaderTitle title="Song Claim" breadcrumbData={breadcrumbData} />
        <ButtonGroupTop />
        <Divider className={classes.flatDivider} />
        <Grid container justifyContent="space-between" my={2}>
          <Grid item>
            <SearchTextInput
              placeholder="Search"
              value={queryParams?.search}
              onChange={e =>
                handleChangeQueryParams(e?.target?.value, "search")
              }
              disabled={loadingPage}
            />
          </Grid>
          <Grid item>
            <Grid container columnSpacing={1} alignItems="center">
              <Grid item>
                <SelectInput
                  label="Filter Type"
                  options={optionType({ rolePublisher })}
                  optionKey="key"
                  optionLabel="name"
                  value={filterParams?.filter_type || ""}
                  onChange={e => handleChangeFilterType(e?.target?.value)}
                  placeholder="None"
                  width={200}
                  disabled={loadingPage}
                />
              </Grid>
              <Grid item>
                <AutoCompleteWithSearch
                  options={optionFilter}
                  optionLabel="label"
                  uniqueKey="value"
                  label={selectedOptionFilter?.name || "Filtered Options"}
                  placeholder={`Search ${selectedOptionFilter?.name ||
                    "Filtered Options"}`}
                  loading={loadingFilter}
                  inputValue={filterParams?.search || ""}
                  onInputChange={e => handleChangeFilterParams(e, "search")}
                  value={
                    optionFilter?.find(
                      option =>
                        option.value ===
                        queryParams?.[selectedOptionFilter?.key]
                    ) || null
                  }
                  onChange={value =>
                    handleChangeQueryParams(
                      value?.id || null,
                      selectedOptionFilter?.key
                    )
                  }
                  setQueryParams={setFilterParams}
                  hasMore={hasMore}
                  width={300}
                  disabled={loadingPage}
                />
              </Grid>
              <Tooltip title="Download Document">
                <Grid item>
                  <PrimaryButton
                    label="Download"
                    disabled={loadingButton || loadingPage}
                    loading={loadingButton}
                    onClick={handleDownloadDocument}
                    size="large"
                  />
                </Grid>
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
        <CustomTable
          isLoading={loadingPage}
          data={dataTable}
          columnTable={columnTable({ rolePublisher, classes })}
          page={queryParams?.page}
          pageSize={queryParams?.size}
          pageCount={tableTotalPage}
          handleChangePage={(_, page) => handleChangeQueryParams(page, "page")}
          handleChangePageSize={e =>
            handleChangeQueryParams(e?.target?.value, "size")
          }
          isHaveAction
          handleEdit={item => {
            handleOpenModal(item?.song_id);
          }}
        />
      </Container>
      {modalVisible && (
        <ModalFormOwnershipConflict
          open={modalVisible}
          preload={modalPreload}
          onClose={handleCloseModal}
          getDataTable={getDataTable}
        />
      )}
    </Page>
  );
};

const defaultKeys = ["page", "size", "search"];
const optionType = ({ rolePublisher }) => [
  { key: "publisher", name: `${rolePublisher ? "Other " : ""} Publisher` },
  { key: "composer", name: "Composer/Author" },
  { key: "iswc", name: "ISWC" },
  { key: "isrc", name: "ISRC" },
];
const columnTable = ({ rolePublisher, classes }) => [
  {
    name: rolePublisher ? "other_party" : "party_name",
    title: `${rolePublisher ? "Other " : ""}Publisher`,
    renderText: item => <ArrayChip xs={12} list={item} />,
  },
  {
    name: "song_title",
    title: "Song Title",
    renderText: item => (
      <Typography className={classes.songTitle}>
        {item?.replace(/, /g, " ; ")}
      </Typography>
    ),
  },
  {
    name: "composers",
    title: "Composer/Author",
    renderText: composer_names => {
      const composers = composer_names?.map(
        ({ composer_name, is_own_data }) => ({
          name: composer_name,
          color: is_own_data ? "white" : "#D1D5DD",
          is_own_data,
        })
      );
      return <ArrayChip xs={12} list={composers} max={2} />;
    },
  },
  {
    name: "iswc_code",
    title: "ISWC",
    renderText: item =>
      item ? (
        <ArrayChip xs={12} list={[item?.replace(/, /g, " ; ")]} max={2} />
      ) : (
        "-"
      ),
  },
  {
    name: "isrc_codes",
    title: "ISRC",
    renderText: iswc_codes => {
      const adjusted_iswc_codes = iswc_codes?.map(
        ({ isrc_code, is_own_data }) => ({
          name: isrc_code,
          color: is_own_data ? "white" : "#D1D5DD",
          is_own_data,
        })
      );
      return <ArrayChip xs={12} list={adjusted_iswc_codes} max={2} />;
    },
  },
  {
    name: "ownership_percentages",
    title: "Ownership Percentage",
    renderText: ownership => {
      const adjusted_ownership = ownership?.map(
        ({ percentage, is_own_publisher }) => ({
          name: `${percentage}%`,
          color: is_own_publisher ? "white" : "#D1D5DD",
          percentage,
        })
      );
      return <ArrayChip xs={12} list={adjusted_ownership} max={2} />;
    },
  },
];

export default OwnershipConflict;
